import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  ArrowRightLeft,
  AlertCircle,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { feedService } from '@/services/feedService';
import { cycleService } from '@/services/cycleService';
import { Combobox } from '@/components/common/Combobox';
import { supabase } from '@/lib/supabaseClient';
import { formatCurrency } from '@/lib/utils';
import {
  FeedProductRecord,
  ProductionCycleRecord,
  FeedReceiptRecord,
} from '@/types/database';

interface FeedTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialSourceCycleId?: string;
}

export const FeedTransferModal: React.FC<FeedTransferModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialSourceCycleId,
}) => {
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [products, setProducts] = useState<FeedProductRecord[]>([]);
  const [loadingLookups, setLoadingLookups] = useState<boolean>(true);

  // Form states
  const [sourceCycleId, setSourceCycleId] = useState<string>('');
  const [destCycleId, setDestCycleId] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [sourceReceiptId, setSourceReceiptId] = useState<string>('');
  const [transferDate, setTransferDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [quantityBags, setQuantityBags] = useState<number>(20);
  const [notes, setNotes] = useState<string>('');

  // Source Receipts & Inventory calculation states
  const [sourceReceipts, setSourceReceipts] = useState<FeedReceiptRecord[]>([]);
  const [availableBagsOnSource, setAvailableBagsOnSource] = useState<number>(0);
  const [calculatingStock, setCalculatingStock] = useState<boolean>(false);

  // Submission & Validation States
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Load active cycles & products
  useEffect(() => {
    if (!isOpen) return;

    const fetchLookups = async () => {
      setLoadingLookups(true);
      setErrorMsg(null);
      try {
        const [cycs, prods] = await Promise.all([
          cycleService.getCycles(),
          feedService.getFeedProducts(),
        ]);

        setCycles(cycs);
        setProducts(prods);

        if (initialSourceCycleId) {
          setSourceCycleId(initialSourceCycleId);
        } else if (cycs.length > 0 && !sourceCycleId) {
          setSourceCycleId(cycs[0].id);
        }

        if (cycs.length > 1 && !destCycleId) {
          const defaultDest = cycs.find((c) => c.id !== (initialSourceCycleId || cycs[0].id));
          if (defaultDest) setDestCycleId(defaultDest.id);
        }

        if (prods.length > 0 && !productId) {
          setProductId(prods[0].id);
        }
      } catch (err: any) {
        console.error('Error fetching lookups for feed transfer:', err);
        setErrorMsg('Failed to load cycles or feed products.');
      } finally {
        setLoadingLookups(false);
      }
    };

    fetchLookups();
  }, [isOpen, initialSourceCycleId]);

  // When source cycle or product changes, calculate available bags and query source receipts for lineage
  useEffect(() => {
    if (!sourceCycleId || !productId) {
      setAvailableBagsOnSource(0);
      setSourceReceipts([]);
      return;
    }

    const loadSourceStock = async () => {
      setCalculatingStock(true);
      try {
        const [receiptsRes, inRes, outRes] = await Promise.all([
          supabase
            .from('feed_receipts')
            .select('*')
            .eq('cycle_id', sourceCycleId)
            .eq('product_id', productId)
            .eq('is_void', false)
            .order('receipt_date', { ascending: false }),
          supabase
            .from('feed_transfers')
            .select('quantity_bags, total_transferred_cost')
            .eq('destination_cycle_id', sourceCycleId)
            .eq('feed_product_id', productId)
            .eq('is_void', false),
          supabase
            .from('feed_transfers')
            .select('quantity_bags, total_transferred_cost')
            .eq('source_cycle_id', sourceCycleId)
            .eq('feed_product_id', productId)
            .eq('is_void', false),
        ]);

        const directBags = (receiptsRes.data || []).reduce(
          (s: number, r: any) => s + Number(r.quantity_bags || 0),
          0
        );
        const inBags = (inRes.data || []).reduce(
          (s: number, t: any) => s + Number(t.quantity_bags || 0),
          0
        );
        const outBags = (outRes.data || []).reduce(
          (s: number, t: any) => s + Number(t.quantity_bags || 0),
          0
        );
        const netAvailable = directBags + inBags - outBags;

        setAvailableBagsOnSource(netAvailable);
        setSourceReceipts((receiptsRes.data || []) as FeedReceiptRecord[]);
      } catch (err: any) {
        console.error('Error calculating source stock:', err);
      } finally {
        setCalculatingStock(false);
      }
    };

    loadSourceStock();
  }, [sourceCycleId, productId]);

  // Calculate Unit Transferred Cost and Total Transferred Cost
  const calculatedUnitCost = useMemo(() => {
    if (sourceReceiptId) {
      const rec = sourceReceipts.find((r) => r.id === sourceReceiptId);
      if (rec && Number(rec.quantity_bags) > 0) {
        return Number(rec.total_cost) / Number(rec.quantity_bags);
      }
    }

    // Weighted average of all source receipts for this product
    const totalCost = sourceReceipts.reduce((sum, r) => sum + Number(r.total_cost || 0), 0);
    const totalBags = sourceReceipts.reduce((sum, r) => sum + Number(r.quantity_bags || 0), 0);
    if (totalBags > 0) {
      return totalCost / totalBags;
    }

    const prod = products.find((p) => p.id === productId);
    return Number(prod?.default_unit_price) || 0;
  }, [sourceReceiptId, sourceReceipts, products, productId]);

  const totalTransferredCost = useMemo(() => {
    return (Number(quantityBags) || 0) * calculatedUnitCost;
  }, [quantityBags, calculatedUnitCost]);

  // Combobox option maps
  const cycleOptions = useMemo(() => {
    return cycles.map((c) => ({
      value: c.id,
      label: `${c.cycle_code} — ${c.growers?.full_name || 'Company-Owned'}`,
      badge: c.status,
    }));
  }, [cycles]);

  const destCycleOptions = useMemo(() => {
    return cycles
      .filter((c) => c.id !== sourceCycleId)
      .map((c) => ({
        value: c.id,
        label: `${c.cycle_code} — ${c.growers?.full_name || 'Company-Owned'}`,
        badge: c.status,
      }));
  }, [cycles, sourceCycleId]);

  const productOptions = useMemo(() => {
    return products.map((p) => ({
      value: p.id,
      label: p.product_name,
    }));
  }, [products]);

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!sourceCycleId) {
      setErrorMsg('Please select a source production cycle.');
      return;
    }
    if (!destCycleId) {
      setErrorMsg('Please select a destination production cycle.');
      return;
    }
    if (sourceCycleId === destCycleId) {
      setErrorMsg('Source and Destination cycles must be different.');
      return;
    }
    if (!productId) {
      setErrorMsg('Please select a feed product.');
      return;
    }
    if (!quantityBags || quantityBags <= 0) {
      setErrorMsg('Quantity to transfer must be greater than 0.');
      return;
    }
    if (quantityBags > availableBagsOnSource && availableBagsOnSource > 0) {
      setErrorMsg(
        `Cannot transfer ${quantityBags} bags. Source cycle only has ${availableBagsOnSource} available bags of this product.`
      );
      return;
    }

    setSubmitting(true);
    try {
      await feedService.createFeedTransfer({
        source_cycle_id: sourceCycleId,
        destination_cycle_id: destCycleId,
        feed_product_id: productId,
        source_feed_receipt_id: sourceReceiptId || null,
        transfer_date: transferDate,
        quantity_bags: Number(quantityBags),
        total_weight_kg: Number(quantityBags) * 50,
        unit_price: calculatedUnitCost,
        notes: notes.trim() || null,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error executing feed transfer:', err);
      setErrorMsg(err.message || 'Failed to execute feed transfer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl my-6 flex flex-col max-h-[92vh] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-3.5 sm:py-4 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-purple-100 text-purple-800 flex-shrink-0">
              <ArrowRightLeft className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900">Cycle-to-Cycle Feed Transfer</h2>
              <p className="text-[11px] sm:text-xs text-slate-500">
                Move feed directly between cycles with exact proportional cost preservation.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            aria-label="Close modal"
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body / Scrollable Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {errorMsg && (
            <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-red-50 text-red-900 border border-red-200 text-xs">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Section 1: Cycle Routing */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Source Cycle (Feed Deduction) <span className="text-red-500">*</span>
              </label>
              <Combobox
                options={cycleOptions}
                value={sourceCycleId}
                onChange={setSourceCycleId}
                placeholder="Select Source Cycle..."
                searchPlaceholder="Search source cycle..."
                disabled={loadingLookups || submitting}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Destination Cycle (Feed Addition) <span className="text-red-500">*</span>
              </label>
              <Combobox
                options={destCycleOptions}
                value={destCycleId}
                onChange={setDestCycleId}
                placeholder="Select Destination Cycle..."
                searchPlaceholder="Search destination cycle..."
                disabled={loadingLookups || submitting}
              />
            </div>
          </div>

          {/* Section 2: Feed Product & Available Stock */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Feed Product <span className="text-red-500">*</span>
              </label>
              <Combobox
                options={productOptions}
                value={productId}
                onChange={setProductId}
                placeholder="Select Feed Product..."
                searchPlaceholder="Search product..."
                disabled={loadingLookups || submitting}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Available on Source
              </label>
              <div className="h-9 px-3 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-between text-xs font-mono font-bold text-slate-800">
                <span>{calculatingStock ? '...' : `${availableBagsOnSource.toLocaleString()} bags`}</span>
                <span className="text-[10px] font-sans font-normal text-slate-500">Live</span>
              </div>
            </div>
          </div>

          {/* Section 3: Transfer Details */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Transfer Date <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                disabled={submitting}
                className="h-9 text-xs bg-white"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Quantity (Bags) <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={quantityBags}
                onChange={(e) => setQuantityBags(Number(e.target.value))}
                disabled={submitting}
                className="h-9 text-xs font-mono font-bold text-slate-900 bg-white"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Transferred Unit Cost
              </label>
              <div className="h-9 px-3 rounded-lg bg-slate-100 border border-slate-200 flex items-center text-xs font-mono font-bold text-slate-800">
                {formatCurrency(calculatedUnitCost)}/bag
              </div>
            </div>
          </div>

          {/* Optional Source Receipt Lineage */}
          {sourceReceipts.length > 0 && (
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Link to Source Delivery Receipt (Optional Lineage)
              </label>
              <select
                value={sourceReceiptId}
                onChange={(e) => setSourceReceiptId(e.target.value)}
                disabled={submitting}
                aria-label="Link to Source Delivery Receipt"
                className="w-full h-9 text-xs rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-slate-800 focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Weighted Average of Source Cycle Receipts</option>
                {sourceReceipts.map((r) => {
                  const unitCost =
                    Number(r.quantity_bags) > 0
                      ? Number(r.total_cost) / Number(r.quantity_bags)
                      : 0;
                  return (
                    <option key={r.id} value={r.id}>
                      Receipt #{r.receipt_number || 'N/A'} • {r.receipt_date} • {r.quantity_bags} bags @ ₱{unitCost.toFixed(2)}/bag {r.invoice_no ? `(Inv #${r.invoice_no})` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Transfer Reason / Notes
            </label>
            <Input
              type="text"
              placeholder="e.g. Excess Starter bags re-allocated before cycle close"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              className="h-9 text-xs bg-white"
            />
          </div>

          {/* Cost Invariance Verification Banner */}
          <div className="p-4 rounded-xl bg-slate-900 text-white font-mono space-y-2">
            <div className="flex items-center justify-between text-xs border-b border-slate-800 pb-2">
              <span className="text-[11px] font-bold uppercase text-purple-400 flex items-center gap-1.5 font-sans">
                <ShieldCheck className="w-4 h-4 text-purple-400" />
                Cost Invariance & Lineage Preservation
              </span>
              <span className="text-slate-400 text-[10px]">Invariant Total</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs pt-1">
              <div>
                <span className="text-slate-400 block text-[10px]">Source Cycle (Credit)</span>
                <span className="text-sm font-bold text-red-400">
                  -{formatCurrency(totalTransferredCost)}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px]">Dest. Cycle (Debit)</span>
                <span className="text-sm font-bold text-emerald-400">
                  +{formatCurrency(totalTransferredCost)}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px]">Net VSB Cost Impact</span>
                <span className="text-sm font-bold text-white">₱0.00</span>
              </div>
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-5 sm:px-6 py-3.5 sm:py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="text-xs h-9 px-4"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loadingLookups}
            className="text-xs h-9 px-5 bg-purple-700 hover:bg-purple-600 text-white font-semibold gap-1.5 shadow-xs"
          >
            {submitting ? 'Transferring Feed...' : `Confirm Transfer (${quantityBags} Bags)`}
          </Button>
        </div>
      </div>
    </div>
  );
};
